import { FC, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import {
  EmployeeWithLatestAndDraftEvaluation,
  ScoreGridDto,
  ScoreGoalType,
  ScoreGoalDto,
  DraftScoreGridInput,
  DraftScoreGoalInput,
  Country,
} from '../../types'
import { PerModal } from '../../components/custom_components/Modal'
import PerText from '../../components/custom_components/Text'
import { computeFullname } from '../../shared/nameDisplayPreferencesUtil'
import { useAuth } from '../../context/auth-context'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { Col, Card } from 'antd'
import PerSpacer from '../../components/custom_components/Spacer'
import { useDraftOrLatestScoreGridByEmployee, createOrUpdateDraftScoreGrid } from '../../graphql/draft-score-grid/useDraftScoreGrid'
import { LoadingAndErrors } from '../../components/custom_components/LoadingAndErrors'
import { useForm } from 'react-hook-form'
import { ScoreGridFormFields } from './ScoreGridsStoreState'
import { DraftScoreGoalModalRow } from './ScoreGoalModalRow'
import { showNotification } from '../../components/custom_components/Notification/notification'

type DraftScoreGridsModalProps = {
  onCancel: () => void
  employeeData: EmployeeWithLatestAndDraftEvaluation
  companyId: string
  country: Country
  isCompanyManager: boolean
}

export const DraftScoreGridsModal: FC<DraftScoreGridsModalProps> = ({ onCancel, employeeData, companyId, country, isCompanyManager }) => {
  const { data, error, loading } = useDraftOrLatestScoreGridByEmployee(companyId, country, employeeData.employee.id)

  if (error) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
  }

  return (
    <>
      {data && (
        <DraftScoreGridsModalContent
          visible={!!data}
          onCancel={onCancel}
          employeeData={employeeData}
          scoreGrid={data.getDraftOrLatestScoreGridByEmployee}
          companyId={companyId}
          country={country}
          isEditMode={isCompanyManager}
        />
      )}
    </>
  )
}

type DraftScoreGridsModalContentProps = {
  visible: boolean
  onCancel: () => void
  employeeData: EmployeeWithLatestAndDraftEvaluation
  scoreGrid: ScoreGridDto | null | undefined
  companyId: string
  country: Country
  isEditMode: boolean
}

export const DraftScoreGridsModalContent: FC<DraftScoreGridsModalContentProps> = ({
  visible,
  onCancel,
  employeeData,
  scoreGrid,
  companyId,
  country,
  isEditMode,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const [isProcessing, toggleProcessing] = useState<boolean>(false)

  const getScoreGoal = (type: ScoreGoalType): number | undefined | null => {
    const scoreGoal: ScoreGoalDto | undefined = scoreGrid ? scoreGrid.scoreGoals.find((sg) => sg.viewId === type) : undefined
    return scoreGoal ? scoreGoal.resultEvaluation : undefined
  }

  const scoreGridForm = useForm<ScoreGridFormFields>({
    defaultValues: {
      Customer_1: getScoreGoal(ScoreGoalType.Customer_1),
      Customer_2: getScoreGoal(ScoreGoalType.Customer_2),
      Customer_3: getScoreGoal(ScoreGoalType.Customer_3),
      Customer_4: getScoreGoal(ScoreGoalType.Customer_4),
      Customer_5: getScoreGoal(ScoreGoalType.Customer_5),
      Speed_1: getScoreGoal(ScoreGoalType.Speed_1),
      Speed_2: getScoreGoal(ScoreGoalType.Speed_2),
      Speed_3: getScoreGoal(ScoreGoalType.Speed_3),
      Teamwork_1: getScoreGoal(ScoreGoalType.Teamwork_1),
      Teamwork_2: getScoreGoal(ScoreGoalType.Teamwork_2),
      Teamwork_3: getScoreGoal(ScoreGoalType.Teamwork_3),
      Excellence_1: getScoreGoal(ScoreGoalType.Excellence_1),
      Excellence_2: getScoreGoal(ScoreGoalType.Excellence_2),
      Excellence_3: getScoreGoal(ScoreGoalType.Excellence_3),
      Innovation_1: getScoreGoal(ScoreGoalType.Innovation_1),
      Innovation_2: getScoreGoal(ScoreGoalType.Innovation_2),
      HumanValues_1: getScoreGoal(ScoreGoalType.HumanValues_1),
      HumanValues_2: getScoreGoal(ScoreGoalType.HumanValues_2),
    },
    criteriaMode: 'all',
    mode: 'onChange',
  })
  const { control, watch, getValues } = scoreGridForm
  watch()

  const createDraftScoreGridInput = (): DraftScoreGridInput => {
    const values = getValues()
    const result: DraftScoreGridInput = {
      id: scoreGrid ? scoreGrid.id : undefined,
      scoreGoals: Object.keys(values).map((type) => {
        const scoreGoalInput: DraftScoreGoalInput = {
          id: scoreGrid ? scoreGrid.scoreGoals.find((sg) => sg.viewId === (type as ScoreGoalType))!.id : undefined,
          viewId: type as ScoreGoalType,
          resultEvaluation: values[type],
        }
        return scoreGoalInput
      }),
    }
    return result
  }

  const saveDraftScoreGrid = async () => {
    if (!!scoreGridForm) {
      const scoreGridInput: DraftScoreGridInput = createDraftScoreGridInput()
      const { errors } = await createOrUpdateDraftScoreGrid(scoreGridInput, companyId, country, employeeData.employee.id)
      if (!!errors && !!errors.length) {
        showNotification({
          type: 'error',
          message: t('draftScoreGrid.modal.notifications.error.title'),
          description: t('draftScoreGrid.modal.notifications.error.description'),
        })
        toggleProcessing(false)
      } else {
        showNotification({
          type: 'success',
          message: t('draftScoreGrid.modal.notifications.success.title'),
          description: t('draftScoreGrid.modal.notifications.success.description', {
            employeeFullName: computeFullname(user.nameDisplayPreference, employeeData.employee.firstName, employeeData.employee.lastName),
          }),
        })
        onCancel()
      }
    }
  }

  const handleSubmit = async () => {
    if (!!scoreGridForm && !!scoreGridForm.trigger) {
      await scoreGridForm.trigger()
      if (!Object.keys(scoreGridForm.formState.errors).length) {
        toggleProcessing(true)
        await saveDraftScoreGrid()
        toggleProcessing(false)
      }
    }
  }

  const buttonDisabled = () => {
    return isProcessing || Object.keys(getValues()).some((key) => getValues()[key] === null || getValues()[key] === undefined)
  }

  return (
    <>
      <PerModal
        size="large"
        title={{
          heading: isEditMode ? t('draftScoreGrid.modal.editHeading') : t('draftScoreGrid.modal.viewHeading'),
        }}
        visible={visible}
        footer={{
          cancelBtn: {
            onClick: () => {
              onCancel()
            },
          },
          customBtns: isEditMode
            ? [
                {
                  text: t('draftScoreGrid.modal.applyChanges'),
                  onClick: () => {
                    handleSubmit()
                  },
                  color: 'success',
                  icon: 'check_stroke',
                  disabled: buttonDisabled(),
                  isProcessing: { active: isProcessing },
                },
              ]
            : undefined,
        }}
        onCancel={() => {
          onCancel()
        }}
      >
        {isEditMode && (
          <PerText color="secondary" appearance="flowing-text" mb="l">
            <Trans
              i18nKey="draftScoreGrid.modal.description"
              values={{
                employeeName: computeFullname(user.nameDisplayPreference, employeeData.employee.firstName, employeeData.employee.lastName),
              }}
              components={[<b></b>]}
            />
          </PerText>
        )}

        <PerFlexRow align="middle">
          <Col span={12}>
            <PerFlexRow direction="column">
              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.Customer')}</PerText>
                  </div>
                </PerFlexRow>
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Customer_1)) || undefined}
                  type={ScoreGoalType.Customer_1}
                  control={control}
                  mb="xs"
                />

                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Customer_2)) || undefined}
                  type={ScoreGoalType.Customer_2}
                  control={control}
                  mb="xs"
                />
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Customer_3)) || undefined}
                  type={ScoreGoalType.Customer_3}
                  control={control}
                  mb="xs"
                />
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Customer_4)) || undefined}
                  type={ScoreGoalType.Customer_4}
                  control={control}
                  mb="xs"
                />
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Customer_5)) || undefined}
                  type={ScoreGoalType.Customer_5}
                  control={control}
                />
              </Card>

              <PerSpacer mb="s" />

              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.Innovation')}</PerText>
                  </div>
                </PerFlexRow>

                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Innovation_1)) || undefined}
                  type={ScoreGoalType.Innovation_1}
                  control={control}
                  mb="xs"
                />
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Innovation_2)) || undefined}
                  type={ScoreGoalType.Innovation_2}
                  control={control}
                />
              </Card>

              <PerSpacer mb="s" />

              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.HumanValues')}</PerText>
                  </div>
                </PerFlexRow>

                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.HumanValues_1)) || undefined}
                  type={ScoreGoalType.HumanValues_1}
                  control={control}
                  mb="xs"
                />
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.HumanValues_2)) || undefined}
                  type={ScoreGoalType.HumanValues_2}
                  control={control}
                />
              </Card>
            </PerFlexRow>
          </Col>

          <Col span={12}>
            <PerFlexRow direction="column">
              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.Speed')}</PerText>
                  </div>
                </PerFlexRow>

                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Speed_1)) || undefined}
                  type={ScoreGoalType.Speed_1}
                  control={control}
                  mb="xs"
                />
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Speed_2)) || undefined}
                  type={ScoreGoalType.Speed_2}
                  control={control}
                  mb="xs"
                />
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Speed_3)) || undefined}
                  type={ScoreGoalType.Speed_3}
                  control={control}
                />
              </Card>

              <PerSpacer mb="s" />

              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.Teamwork')}</PerText>
                  </div>
                </PerFlexRow>

                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Teamwork_1)) || undefined}
                  type={ScoreGoalType.Teamwork_1}
                  control={control}
                  mb="xs"
                />
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Teamwork_2)) || undefined}
                  type={ScoreGoalType.Teamwork_2}
                  control={control}
                  mb="xs"
                />
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Teamwork_3)) || undefined}
                  type={ScoreGoalType.Teamwork_3}
                  control={control}
                />
              </Card>

              <PerSpacer mb="s" />

              <Card>
                <PerFlexRow align="middle" justify="center" mb="xs">
                  <div className="tab-bar__tab tab-bar__tab--active" style={{ paddingTop: 0, cursor: 'default' }}>
                    <PerText>{t('ownGrowthView.grid.category.Excellence')}</PerText>
                  </div>
                </PerFlexRow>
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Excellence_1)) || undefined}
                  type={ScoreGoalType.Excellence_1}
                  control={control}
                  mb="xs"
                />
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Excellence_2)) || undefined}
                  type={ScoreGoalType.Excellence_2}
                  control={control}
                  mb="xs"
                />
                <DraftScoreGoalModalRow
                  isEditMode={isEditMode}
                  sgValue={(!isEditMode && getScoreGoal(ScoreGoalType.Excellence_3)) || undefined}
                  type={ScoreGoalType.Excellence_3}
                  control={control}
                />
              </Card>
            </PerFlexRow>
          </Col>
        </PerFlexRow>
      </PerModal>
    </>
  )
}
